import React, { useState } from 'react';
import { HiOutlineDownload } from 'react-icons/hi';
import axiosInstance from '../../api/axiosConfig';
import { STATUS_OPTIONS } from './DeliveryIndicators';
import './ExportModal.css';

const ExportModal = ({ onClose, currentFilters }) => {
    const [loading, setLoading] = useState(false);
    const [exportConfig, setExportConfig] = useState({
        format: 'excel',
        dateFrom: currentFilters.dateFrom || '',
        dateTo: currentFilters.dateTo || '',
        status: currentFilters.status || '',
        paymentStatus: currentFilters.paymentStatus || '',
        invoicePending: currentFilters.invoicePending || '',
        deliveryBoy: currentFilters.deliveryBoy || '',
        area: currentFilters.area || '',
        // Admin tenant scope — empty for normal users, ignored server-side for them
        company: currentFilters.company || '',
        branch: currentFilters.branch || ''
    });

    const handleExport = async () => {
        try {
            setLoading(true);

            // Make API call to export
            const response = await axiosInstance.post('/api/deliveries/export', exportConfig, {
                responseType: 'blob' // Important for file download
            });

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            // Set filename based on format
            const dateRange = exportConfig.dateFrom && exportConfig.dateTo
                ? `${exportConfig.dateFrom}_to_${exportConfig.dateTo}`
                : 'All';
            const extension = exportConfig.format === 'excel' ? 'xlsx' : 'pdf';
            link.setAttribute('download', `Delivery_Report_${dateRange}.${extension}`);
            
            // Trigger download
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            // Cleanup
            window.URL.revokeObjectURL(url);
            
            alert('Report downloaded successfully!');
            onClose();
        } catch (error) {
            console.error('Error exporting report:', error);
            alert('Failed to export report. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="export-modal-overlay" onClick={onClose}>
            <div className="export-modal" onClick={(e) => e.stopPropagation()}>
                <div className="export-modal-header">
                    <h2>Export Delivery Report</h2>
                    <button className="export-modal-close" onClick={onClose}>×</button>
                </div>

                <div className="export-modal-body">
                    <div className="export-section">
                        <h3>Report Format</h3>
                        <div className="export-format-options">
                            <label className={`export-format-card ${exportConfig.format === 'excel' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="format"
                                    value="excel"
                                    checked={exportConfig.format === 'excel'}
                                    onChange={(e) => setExportConfig({...exportConfig, format: e.target.value})}
                                />
                                <div className="export-format-content">
                                    <div className="export-format-icon">📊</div>
                                    <div className="export-format-title">Excel</div>
                                    <div className="export-format-desc">
                                        Multiple sheets with detailed analysis
                                    </div>
                                </div>
                            </label>
                            <label className={`export-format-card ${exportConfig.format === 'pdf' ? 'selected' : ''}`}>
                                <input
                                    type="radio"
                                    name="format"
                                    value="pdf"
                                    checked={exportConfig.format === 'pdf'}
                                    onChange={(e) => setExportConfig({...exportConfig, format: e.target.value})}
                                />
                                <div className="export-format-content">
                                    <div className="export-format-icon">📄</div>
                                    <div className="export-format-title">PDF</div>
                                    <div className="export-format-desc">
                                        Professional document for printing
                                    </div>
                                </div>
                            </label>
                        </div>
                    </div>

                    <div className="export-section">
                        <h3>Date Range</h3>
                        <div className="export-date-inputs">
                            <div className="export-input-group">
                                <label>From Date</label>
                                <input
                                    type="date"
                                    value={exportConfig.dateFrom}
                                    onChange={(e) => setExportConfig({...exportConfig, dateFrom: e.target.value})}
                                    className="export-input"
                                />
                            </div>
                            <div className="export-input-group">
                                <label>To Date</label>
                                <input
                                    type="date"
                                    value={exportConfig.dateTo}
                                    onChange={(e) => setExportConfig({...exportConfig, dateTo: e.target.value})}
                                    className="export-input"
                                />
                            </div>
                        </div>
                        {!exportConfig.dateFrom && !exportConfig.dateTo && (
                            <p className="export-hint">Leave empty to export all deliveries</p>
                        )}
                    </div>

                    <div className="export-section">
                        <h3>Filters (Optional)</h3>
                        <div className="export-filters">
                            <div className="export-input-group">
                                <label>Status</label>
                                <select
                                    value={exportConfig.status}
                                    onChange={(e) => setExportConfig({...exportConfig, status: e.target.value})}
                                    className="export-input"
                                >
                                    <option value="">All Status</option>
                                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div className="export-input-group">
                                <label>Payment</label>
                                <select
                                    value={exportConfig.paymentStatus}
                                    onChange={(e) => setExportConfig({...exportConfig, paymentStatus: e.target.value})}
                                    className="export-input"
                                >
                                    <option value="">All Payments</option>
                                    <option value="Paid">Paid</option>
                                    <option value="Partial">Partially Paid</option>
                                    <option value="Unpaid">Unpaid</option>
                                    <option value="NotPaid">Not Fully Paid (Unpaid + Partial)</option>
                                </select>
                            </div>
                            <div className="export-input-group">
                                <label>Invoice</label>
                                <select
                                    value={exportConfig.invoicePending}
                                    onChange={(e) => setExportConfig({...exportConfig, invoicePending: e.target.value})}
                                    className="export-input"
                                >
                                    <option value="">All Invoices</option>
                                    <option value="true">Invoice Pending</option>
                                    <option value="false">Invoice Not Pending</option>
                                </select>
                            </div>
                            <div className="export-input-group">
                                <label>Area</label>
                                <input
                                    type="text"
                                    placeholder="Filter by area"
                                    value={exportConfig.area}
                                    onChange={(e) => setExportConfig({...exportConfig, area: e.target.value})}
                                    className="export-input"
                                />
                            </div>
                        </div>
                    </div>

                    {exportConfig.format === 'excel' && (
                        <div className="export-info-box">
                            <h4>📋 Excel Report Includes:</h4>
                            <ul>
                                <li>Summary - Overview statistics and success rates</li>
                                <li>Detailed List - All delivery records with full details</li>
                                <li>Performance by Delivery Boy - Individual performance metrics</li>
                                <li>By Status - Breakdown by delivery status</li>
                                <li>By Location - Analysis by delivery areas</li>
                            </ul>
                        </div>
                    )}
                </div>

                <div className="export-modal-footer">
                    <button 
                        className="btn btn-secondary" 
                        onClick={onClose}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        className="btn btn-primary" 
                        onClick={handleExport}
                        disabled={loading}
                    >
                        {loading ? (
                            <>
                                <span className="spinner-small"></span>
                                Generating...
                            </>
                        ) : (
                            <>
                                <HiOutlineDownload /> Download Report
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ExportModal;
